'use client'
import { BLOCKS } from '@contentful/rich-text-types'
import { TypographyLongForm } from 'components/Contentful'
import styles from './StickySubNavSection.module.scss'
import { useLazyQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { normalizeContent } from 'utils/shared/hacks'
import { replaceGlobalVars } from 'utils/shared'
import { GET_CONTENT_BY_ID, GET_GLOBAL_VARIABLES } from 'utils/contentful/GraphqlQueries'
import dynamic from 'next/dynamic'
import { SITE_ENVIRONMENTS } from 'constants/websites'
import { FdrTheme } from '@achieve/ascend'
const AchieveSkeleton = dynamic(() => import('components/AchieveSkeleton/AchieveSkeleton'))
const AAL_ALLOWED_PAGES = ['/privacy/policies', '/policies']

const StickySubNavSection = ({ sectionContent, index, addToRefs, makeTestId, pageTestIdScope }) => {
  const { dynamicPathname } = useAppRouter()
  const [isAAL, setIsAAL] = useState(false)
  const isAchieve = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT === SITE_ENVIRONMENTS.ACHIEVE
  const isAllowedPage = AAL_ALLOWED_PAGES.includes(dynamicPathname)
  const sectionName = sectionContent?.fields?.name
  const showAALDisclosure =
    isAllowedPage && isAchieve && isAAL && sectionName?.toLowerCase() === 'disclosures'

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      setIsAAL(urlParams.get('isAAL') === 'true')
    }
  }, [])

  let VARIANT_OVERRIDE = {
    [BLOCKS.HEADING_2]: 'headingLg',
    [BLOCKS.HEADING_3]: 'displayMd',
    [BLOCKS.HEADING_4]: 'bodyLg',
    [BLOCKS.PARAGRAPH]: 'bodySm',
  }

  let MOBILE_VARIANT_OVERRIDE = {
    [BLOCKS.HEADING_2]: 'displayMd',
    [BLOCKS.HEADING_3]: 'bodyLg',
    [BLOCKS.HEADING_4]: 'bodySm',
    [BLOCKS.PARAGRAPH]: 'bodySm',
  }

  let STYLE_OVERRIDE = {
    [BLOCKS.HEADING_2]: { marginBottom: '16px' },
    [BLOCKS.HEADING_3]: { marginBottom: '36px', marginTop: '36px' },
    [BLOCKS.HEADING_4]: { marginBottom: '36px', marginTop: '36px' },
    [BLOCKS.PARAGRAPH]: { marginBottom: '16px' },
  }

  if (process.env.NEXT_PUBLIC_SITE_ENVIRONMENT === SITE_ENVIRONMENTS.FDR) {
    VARIANT_OVERRIDE = {
      [BLOCKS.HEADING_1]: 'headingLg',
      [BLOCKS.HEADING_2]: 'headingMd',
      [BLOCKS.HEADING_3]: 'headingMd',
      [BLOCKS.HEADING_4]: 'headingSm',
      [BLOCKS.HEADING_5]: 'headingSm',
      [BLOCKS.HEADING_6]: 'displayXs',
      [BLOCKS.PARAGRAPH]: 'bodySm',
    }

    MOBILE_VARIANT_OVERRIDE = {
      [BLOCKS.HEADING_1]: 'headingLg',
      [BLOCKS.HEADING_2]: 'headingMd',
      [BLOCKS.HEADING_3]: 'headingMd',
      [BLOCKS.HEADING_4]: 'headingSm',
      [BLOCKS.HEADING_5]: 'headingSm',
      [BLOCKS.HEADING_6]: 'displaySm',
      [BLOCKS.PARAGRAPH]: 'bodySm',
    }

    STYLE_OVERRIDE = {
      [BLOCKS.HEADING_1]: {
        marginBottom: '16px',
        color: FdrTheme.palette.primary.main,
        textAlign: 'center',
        fontWeight: 400,
      },
      [BLOCKS.HEADING_2]: {
        marginBottom: '16px',
        color: FdrTheme.palette.primary.main,
        fontWeight: 400,
      },
      [BLOCKS.HEADING_3]: {
        marginBottom: '36px',
        marginTop: '36px',
        fontWeight: 400,
      },
      [BLOCKS.HEADING_4]: {
        marginBottom: '8px',
        color: FdrTheme.palette.primary.main,
        fontWeight: 400,
      },
      [BLOCKS.HEADING_5]: {
        marginBottom: '8px',
        marginTop: '32px',
        color: FdrTheme.palette.primary.main,
        fontWeight: 400,
        textDecoration: 'underline',
      },
      [BLOCKS.PARAGRAPH]: { marginBottom: '16px' },
    }
  }

  return (
    <div
      ref={(el) => addToRefs(el, index)}
      className={styles['body-text-section']}
      id={sectionContent.fields.name.replaceAll(' ', '-')}
      data-section-index={index}
      key={sectionContent?.sys?.id}
      data-testid={makeTestId(
        `subsection-container-${sectionContent.fields.name.replaceAll(' ', '-')}`,
        pageTestIdScope
      )}
    >
      {sectionContent?.fields?.textContent && (
        <TypographyLongForm
          className={styles['table-custom']}
          breakpoint="lg"
          responsive={true}
          content={sectionContent?.fields?.textContent}
          variantOverride={VARIANT_OVERRIDE}
          mobileVariantOverride={MOBILE_VARIANT_OVERRIDE}
          styleOverride={STYLE_OVERRIDE}
        />
      )}
      {showAALDisclosure && (
        <p
          className="font-normal mb-5 scroll-mt-[72px] text-sm leading-[170%]"
          data-testid="aal-disclosure-content"
        >
          <strong>Achieve Acceleration Loans</strong> - are available through Achieve Personal Loans
          are originated by Cross River Bank, Delaware Branch, Equal Housing Lender All loan and
          rate terms are subject to eligibility restrictions, application review, loan amount, loan
          term, lender approval, and credit review. Eligibility for a loan is not guaranteed. Loans
          are not available in all states. Origination fee of 5.99% charged on accepted loans –
          please call an Achieve Personal Loans representative for further details.
        </p>
      )}
    </div>
  )
}

const StickySubNavSectionScroll = ({
  sectionContent,
  setSectionContents,
  scrollRender = false,
  ...props
}) => {
  const [fetchTextContent] = useLazyQuery(GET_CONTENT_BY_ID)
  const [fetchGlobalVariables] = useLazyQuery(GET_GLOBAL_VARIABLES)
  const [sectionContentData, setSectionContentData] = useState(normalizeContent(sectionContent))

  useEffect(() => {
    processFetchTextContent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const processFetchTextContent = async () => {
    const sectionData = sectionContent
    const { data } = await fetchTextContent({
      variables: {
        id: sectionContent.fields.sys.id,
      },
    })
    sectionData.fields.textContent = data?.contentTypeRichText?.textContent
    const getGlobalVariables = await fetchGlobalVariables()
    const globalVariables =
      getGlobalVariables?.data?.jsonFieldCollection?.items?.[0]?.jsonContent || {}
    setSectionContentData(replaceGlobalVars(normalizeContent(sectionData), globalVariables))
    setSectionContents(replaceGlobalVars(normalizeContent(sectionData), globalVariables))
  }

  if (scrollRender && !sectionContentData.fields.textContent) {
    return <AchieveSkeleton height={300} />
  }

  return (
    <StickySubNavSection
      sectionContent={sectionContentData}
      scrollRender={scrollRender}
      {...props}
    />
  )
}

export { StickySubNavSection, StickySubNavSectionScroll }
export default StickySubNavSection
